class UpsellProduct extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('[data-upsell-add-btn]');
    this.errorEl = this.querySelector('[data-upsell-error]');
    this.btnPrice = this.querySelector('[data-upsell-btn-price]');
    this.upsellVariantId = this.dataset.upsellVariantId;

    // Variant picker setup
    this.variantSelects = this.querySelectorAll('[data-upsell-option-select]');
    const variantsJson = this.querySelector('[data-upsell-variants-json]');
    this.variants = variantsJson ? JSON.parse(variantsJson.textContent) : null;

    if (this.variantSelects.length && this.variants) {
      this.variantSelects.forEach(select => {
        select.addEventListener('change', this.handleVariantChange.bind(this));
      });
      this.updateSelectedVariant();
    }

    if (this.button) {
      this.button.addEventListener('click', this.handleClick.bind(this));
    }
  }

  handleVariantChange() {
    this.updateSelectedVariant();
  }

  updateSelectedVariant() {
    const selectedOptions = Array.from(this.variantSelects).map(s => s.value);

    const matched = this.variants.find(v =>
      v.options.every((opt, i) => opt === selectedOptions[i])
    );

    if (matched) {
      this.upsellVariantId = matched.id.toString();
      this.button.disabled = !matched.available;

      if (this.btnPrice) {
        // Format price using Shopify money format if available, else divide by 100
        const formatted = window.Shopify?.formatMoney
          ? window.Shopify.formatMoney(matched.price, window.theme?.money_format || '${{amount}}')
          : '$' + (matched.price / 100).toFixed(2);
        this.btnPrice.textContent = formatted;
      }

      const addSpan = this.button.querySelector('.upsell-block__btn-text--add');
      if (addSpan) {
        addSpan.style.opacity = matched.available ? '1' : '0.5';
      }
    }
  }

  getMainVariantId() {
    const variantInput = document.querySelector('form[action="/cart/add"] [name="id"]');
    if (variantInput?.value) return variantInput.value;

    const params = new URLSearchParams(window.location.search);
    if (params.get('variant')) return params.get('variant');

    const productJson = document.querySelector('[data-product-json]');
    if (productJson) {
      try {
        const data = JSON.parse(productJson.textContent);
        return data.selected_or_first_available_variant?.id?.toString();
      } catch (e) {}
    }

    return null;
  }

  async isVariantInCart(variantId) {
    const res = await fetch('/cart.js', {
      headers: { 'Content-Type': 'application/json' }
    });
    const cart = await res.json();
    return cart.items.some(item => item.variant_id.toString() === variantId.toString());
  }

  async addToCart(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });

    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || 'Could not add to cart.');
    }

    return res.json();
  }

  refreshCart() {
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        document.querySelectorAll('[data-cart-count]').forEach(el => {
          el.textContent = cart.item_count;
        });
        document.querySelectorAll('[data-cart-total]').forEach(el => {
          el.textContent = window.Shopify?.formatMoney
            ? window.Shopify.formatMoney(cart.total_price, window.theme?.money_format)
            : cart.total_price;
        });
        document.querySelectorAll('cart-count').forEach(el => {
          if (typeof el.update === 'function') el.update(cart);
        });
      })
      .catch(() => {});

    const cartDrawer =
      document.querySelector('cart-drawer') ||
      document.querySelector('cart-notification');

    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      fetch(`${window.Shopify?.routes?.root ?? '/'}?section_id=cart-drawer`)
        .then(r => r.text())
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const sectionHtml = doc.querySelector('#shopify-section-cart-drawer')?.innerHTML;
          if (sectionHtml) {
            cartDrawer.renderContents({ sections: { 'cart-drawer': sectionHtml } });
          }
        })
        .catch(() => {});
    }
  }

  setLoading(isLoading) {
    if (!this.button) return;
    this.button.classList.toggle('loading', isLoading);
    this.button.disabled = isLoading;
  }

  showError(msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg;
    this.errorEl.style.display = 'block';
    setTimeout(() => {
      this.errorEl.style.display = 'none';
    }, 4000);
  }

  async handleClick() {
    this.setLoading(true);
    if (this.errorEl) this.errorEl.style.display = 'none';

    try {
      const mainVariantId = this.getMainVariantId();

      if (!mainVariantId) {
        this.showError('Please select a variant first.');
        return;
      }

      if (!this.upsellVariantId) {
        this.showError('Upsell product is unavailable.');
        return;
      }

      const mainAlreadyInCart = await this.isVariantInCart(mainVariantId);

      const itemsToAdd = mainAlreadyInCart
        ? [{ id: parseInt(this.upsellVariantId), quantity: 1 }]
        : [
            { id: parseInt(mainVariantId), quantity: 1 },
            { id: parseInt(this.upsellVariantId), quantity: 1 }
          ];

      await this.addToCart(itemsToAdd);
      this.refreshCart();

    } catch (err) {
      this.showError(err.message || 'Something went wrong. Please try again.');
      console.error('[UpsellProduct]', err);
    } finally {
      this.setLoading(false);
    }
  }
}

customElements.define('upsell-product', UpsellProduct);